import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import ConfirmDialog from "../../components/ConfirmDialog";

const BranchesList = () => {
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);

  const [branchName, setBranchName] = useState("");
  const [address, setAddress] = useState("");
  const [error, setError] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [editBranch, setEditBranch] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getBranchList();
  }, []);

  const handleAddBranch = async () => {
    try {
      setSaving(true);
      if (!branchName.trim()) {
        setError("Branch name is required");
        setSaving(false)
        return;
      }

      const payload = {
        name: branchName,
        address: address,
      };

      let path = "/branches/create";

      if (editBranch) {
        path = "/branches/update/" + selectedBranchId;
      }

      const response = await axiosInstance.post(path, payload);

      toast.success(response?.data?.message || "Branch saved succesfully");

      getBranchList();
      closeAddBranchPopup();
      setSaving(false);
    } catch (error) {
      setSaving(false);
      toast.error(
        error.response?.data?.message ||
          "Something went wrong, try again after sometime"
      );
    }
  };

  const closeAddBranchPopup = () => {
    setOpen(false);
    setError("");
    setBranchName("");
    setAddress("");
    setEditBranch(false);
  };

  const getBranchList = async () => {
    try {
      const response = await axiosInstance.get("/branches");

      setBranches(response?.data?.list);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error retrieving list");
    }
  };

  const openEditBranchPopup = (branch) => {
    setEditBranch(true);
    setSelectedBranchId(branch?._id);
    setBranchName(branch?.name);
    setAddress(branch?.address);
    setOpen(true);
  };

  const handleDeleteBranch = async () => {
    try {
      await axiosInstance.delete("branches/" + selectedBranchId);
      getBranchList();
      setOpenDelete(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const getRowStyle = () => {
    const leftBorderColor = '#6366f1'; // Premium Indigo
    const bgColor = 'rgba(99, 102, 241, 0.005)';
    const borderColor = 'rgba(99, 102, 241, 0.08)';
    const hoverBg = 'rgba(99, 102, 241, 0.02) !important';
    const hoverBorderColor = 'rgba(99, 102, 241, 0.18)';

    return {
      backgroundColor: bgColor,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
      '& td': {
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: borderColor,
      },
      '& td:first-of-type': {
        borderLeft: '1px solid',
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
        borderColor: borderColor,
        borderLeftColor: `${leftBorderColor} !important`,
        borderLeftWidth: '4px !important',
      },
      '& td:last-of-type': {
        borderRight: '1px solid',
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
        borderColor: borderColor,
      },
      "&:hover": {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
        backgroundColor: hoverBg,
        '& td': {
          borderColor: hoverBorderColor,
        }
      }
    };
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Branches
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            fontWeight: 600,
            px: 3,
            py: 1,
            background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 16px rgba(99, 102, 241, 0.3)',
            },
            '&:active': {
              transform: 'translateY(0)',
            }
          }}
        >
          Add Branch
        </Button>
      </Box>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 'none', backgroundColor: 'transparent' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ '& th': { border: 'none', backgroundColor: 'transparent', color: 'text.secondary', py: 1 } }}>
                <TableCell>
                  <b>Branch Name</b>
                </TableCell>
                <TableCell>
                  <b>Address</b>
                </TableCell>
                <TableCell>
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {branches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No branches found
                  </TableCell>
                </TableRow>
              ) : (
                branches.map((branch) => {
                  const rowStyle = getRowStyle();
                  return (
                    <TableRow key={branch._id || branch.id} sx={rowStyle}>
                      <TableCell sx={{ py: 1.5, fontWeight: 600, color: 'text.primary' }}>
                        {branch.name}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {branch.address || "-"}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <IconButton
                          size="small"
                          sx={{
                            color: '#4f46e5',
                            backgroundColor: 'rgba(99, 102, 241, 0.05)',
                            '&:hover': {
                              backgroundColor: 'rgba(99, 102, 241, 0.15)',
                              transform: 'scale(1.05)'
                            },
                            marginRight: 1,
                            transition: 'all 0.2s',
                          }}
                          onClick={() => openEditBranchPopup(branch)}
                        >
                          <FaEdit size={15} />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{
                            color: '#ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.05)',
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 0.15)',
                              transform: 'scale(1.05)'
                            },
                            transition: 'all 0.2s',
                          }}
                          onClick={() => {
                            setSelectedBranchId(branch?._id);
                            setOpenDelete(true);
                          }}
                        >
                          <FaTrash size={13} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Branch Dialog */}
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle
          variant="h6"
          fontWeight="bold"
          sx={{
            background: "#f5f5f5",
          }}
        >
          {editBranch ? "Edit Branch" : "Add Branch"}
        </DialogTitle>

        <DialogContent>
          <TextField
            label="Branch Name"
            required
            fullWidth
            margin="normal"
            value={branchName}
            onChange={(e) => setBranchName(e.target.value)}
            error={!!error}
            helperText={error}
          />

          <TextField
            label="Address"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => closeAddBranchPopup()}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddBranch}
            disabled={saving}
          >
            {saving ? "Saving" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDelete}
        onClose={()=>setOpenDelete(false)}
        onConfirm={handleDeleteBranch}
        message={`Are you sure you want to delete Branch?`}
      />
    </Box>
  );
};

export default BranchesList;